// src/pages/AiInterview.js
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";   // ← 追加
import CustomButton from "../components/CustomButton";
import { Slider, Checkbox } from "@carbon/react";
import CustomModal from "../components/CustomModal";
import api from "../api";
import "./AiInterview.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// ★ スキーマ（DBキー正準）
import {
  FIELD,
  keyToGroup,       // qid -> 大グループ名（"歩行機能" など）
} from "../schema/aiInterviewSchema";

dayjs.extend(utc);
dayjs.extend(timezone);

// === patient_infoを組み立てる関数 ===
const buildPatientInfo = (preData, startInfo, nonAmbulatory = false) => ({
  age: startInfo?.age ?? "",
  gender: startInfo?.gender_type ?? "",
  edss_score: startInfo?.edss ?? "",
  main_activities: preData?.main_activities ?? "",
  activity_detail: preData?.activity_detail ?? "",
  living_with: preData?.living_with ?? "",
  exercise: preData?.exercise ?? "",
  spend_dayoff: preData?.spend_dayoff ?? "",
  you_made_happy: preData?.you_made_happy ?? "",
  concerning_symptoms: preData?.concerning_symptoms ?? "",
  symptoms_duration: preData?.symptoms_duration ?? "",
  concerns_or_questions: preData?.concerns_or_questions ?? "",
  date_of_answer: preData?.date_of_answer ?? "",
  non_ambulatory: nonAmbulatory ? 1 : 0,
});

/* ========================= 本画面用・質問テキスト辞書 ========================= */ 
const activeQuestions = [
  { qid: "msws1" },
  { qid: "msws2" },
  { qid: "msws3" },
  { qid: "amsq" },
  { qid: "mfis_cog" },
  { qid: "mfis_phy" },
  { qid: "mfis_soc" },
  { qid: "msnq_att" },
  { qid: "msnq_mem" },
  { qid: "msnq_other" },
  { qid: "msnq_pb" },
];

const toCategoryId = (qid) =>
  qid
    .replace(/^msws(\d+)/i, (_, n) => `${n}MSWS`)
    .replace(/^mfis_cog$/i, "MFIS_COG")
    .replace(/^mfis_phy$/i, "MFIS_PHY")
    .replace(/^mfis_soc$/i, "MFIS_SOC")
    .replace(/^msnq_att$/i, "MSNQ_ATT")
    .replace(/^msnq_mem$/i, "MSNQ_MEM")
    .replace(/^msnq_other$/i, "MSNQ_OTH")
    .replace(/^msnq_pb$/i, "MSNQ_PER")
    .replace(/^amsq$/i, "AMSQ");

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    mql.addEventListener?.("change", onChange) ?? mql.addListener(onChange);
    return () => mql.removeEventListener?.("change", onChange) ?? mql.removeListener(onChange);
  }, [query]);
  return matches;
}

export default function AiInterview() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { preData, nonAmbulatory } = state ?? {};
  const isMobile = useMediaQuery("(max-width: 672px)");
  const confirmPrimaryRef = useRef(null);

  const [startInfo, setStartInfo] = useState(null);
  const { user } = useAuth();

  const patientInfo = useMemo(
    () => (startInfo ? buildPatientInfo(preData, startInfo, nonAmbulatory) : null),
    [preData, startInfo, nonAmbulatory]
  );

  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    const fetchStartAi = async () => {
      try {
        const res = await api.get(`/v1/patient/start_ai/${user.id}`);
        if (res.status === 200) {
          setStartInfo(res.data);
          console.log("start_ai 成功:", res.data);
        } else {
          console.warn("start_ai 異常:", res.status, res.data);
        }
      } catch (err) {
        console.error("start_ai エラー:", err.message);
      }
    };
    fetchStartAi();
  }, [user?.id]);

  // --- 直リンク対策 ---
  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!state?.preData) {
      navigate("/pre-interview");
      return;
    }
  }, [user, state, navigate]);

  /** 質問再生成の上限 */
  const MAX_REGEN_PER_Q = 2;

  // ★ 先頭3問（MSWS）を非歩行ならスキップ。ただし表示番号は通し（全11問）
  const baseOffset = nonAmbulatory ? 3 : 0;
  const totalOriginal = activeQuestions.length; // 11
  const effectiveQuestions = useMemo(() => {
    return nonAmbulatory ? activeQuestions.slice(3) : activeQuestions;
  }, [nonAmbulatory]);

  // const effectiveQuestions = nonAmbulatory ? activeQuestions.slice(3) : activeQuestions;

  const didInitRef = useRef(false);

useEffect(() => {
  if (!patientInfo || !effectiveQuestions.length) return;

  // ✅ StrictMode(開発)の二重実行や、再レンダーによる再実行を防ぐ
  if (didInitRef.current) return;
  didInitRef.current = true;

  const initQuestion = async () => {
    setIsGenerating(true);
    const firstQid = effectiveQuestions[0].qid;

    const categoryId = toCategoryId(firstQid);

    try {
      const selectRes = await api.post("/v1/questions/select", {
        category_id: categoryId,
        prohibited_list: [],
        patient_info: patientInfo,
      });
      const subId = selectRes.data.question_id;

      const personalizeRes = await api.post("/v1/questions/personalize", {
        category_id: categoryId,
        sub_id: subId,
        patient_info: patientInfo,
        prev_q_list: [],
      });

      const newText = personalizeRes.data.personalized_question;
      setQuestionStates({
        [firstQid]: { text: newText, subId, categoryId, regenCount: 0, prevTexts: [] },
      });
      // setQuestionStates({
      //   [firstQid]: { text: newText, subId, categoryId, regenCount: 0 },
      // });
    } catch (err) {
      console.error("初回質問生成失敗:", err);
      // 失敗時に「再試行できる」ように戻すならここで didInitRef.current=false にする設計もあり
    } finally {
      setIsGenerating(false);
    }
  };

  initQuestion();
}, [patientInfo, effectiveQuestions]);

  // state 管理
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState(() => Object.fromEntries(effectiveQuestions.map((q) => [q.qid, undefined])));
  const [skipped, setSkipped] = useState({});
  const [sliderValues, setSliderValues] = useState(() => Object.fromEntries(effectiveQuestions.map((q) => [q.qid, 5])));
  const [disabledSliders, setDisabledSliders] = useState({});
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [regenCounts, setRegenCounts] = useState(() => Object.fromEntries(effectiveQuestions.map((q) => [q.qid, 0])));
  const [questionStates, setQuestionStates] = useState({});

  const currentQuestion = effectiveQuestions[currentIndex];
  const currentQid = currentQuestion.qid;
  const positionOriginal = baseOffset + currentIndex + 1;

  // --- 最終送信 ---
  const handleFinalSubmit = async () => {
    setConfirmOpen(false);

    // === 正しい患者IDを取得 ===
    const storedPatient = sessionStorage.getItem("patient");
    const patient = storedPatient ? JSON.parse(storedPatient) : null;
    const research_id = patient?.research_id || user?.id || "P010";
    
    const selfEvalRaw = preData?.radio_question;
    // int で渡す（NaN は null に落とす）
    const selfEval = Number.isFinite(Number(selfEvalRaw))
      ? Number(selfEvalRaw)
      : null;

    // === 有効な質問一覧を loop して payload 構築 ===
    const payload = {
      research_id,
      ai_answer_date: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DD"),
      // ai_answer_date: new Date().toISOString().slice(0, 10),
      // ✅ 追加：preData の radioValue を self_eval としてAPIへ渡す
      self_eval: selfEval,
    };

    for (const q of activeQuestions) {
      const qid = q.qid;                           // msws1, mfis_cog, ...
      // const answerVal = answers[qid] ?? sliderValues[qid] ?? "";
      // const regenVal = regenCounts[qid] ?? 0;
      // const subVal = questionStates[qid]?.subId ?? "";
      // const questionText = questionStates[qid]?.text ?? "";

      const isSkipped = !!skipped[qid]; // ★ スキップ判定

      const regenVal = regenCounts[qid] ?? 0;
      const subVal = questionStates[qid]?.subId ?? null;
      const questionText = questionStates[qid]?.text ?? "";

      // スキップなら answer/sub を null、それ以外は値
      const answerVal = isSkipped ? null : (answers[qid] ?? sliderValues[qid] ?? null);
      const subToSend = isSkipped ? null : subVal;

      // ★ カラム名はDB定義に従って接頭辞で結合
      payload[`ai_interview_${qid}_question`] = questionText;
      payload[`ai_interview_${qid}_answer`] = answerVal;
      payload[`ai_interview_${qid}_regen`] = regenVal;
      // payload[`ai_interview_${qid}_sub`] = subVal;
      payload[`ai_interview_${qid}_sub`] = subToSend;
    }

    console.log("送信payload:", payload);

    try {
      const res = await api.post("/v1/patient/save_patient_interview", payload);
      console.log("save_patient_interview 成功:", res.status, res.data);
      navigate("/summary", { replace: true });
      setTimeout(() => navigate("/thanks"), 0);
    } catch (err) {
      console.error("save_patient_interview 失敗:", err);
      alert("保存に失敗しました");
    }
  };

  // ★ モバイル固定バーの高さ計測 → ボックス下paddingへ反映
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(0);

  useEffect(() => {
    if (!isMobile) {
      setBarHeight(0);
      return;
    }
    const el = barRef.current;
    if (!el) return;
    const update = () => setBarHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [isMobile]);

  const [trackWidth, setTrackWidth] = useState(640);

  // effectiveQuestions が変わった場合の不足キー補完（戻る操作などでも安全）
  useEffect(() => {
    setRegenCounts((prev) => {
      const next = { ...prev };
      for (const q of effectiveQuestions) {
        if (!(q.qid in next)) next[q.qid] = 0;
      }
      // 逆に存在しないqidは残しても害はないが、気になるなら削除してもOK
      return next;
    });
    setAnswers((prev) => {
      const base = { ...prev };
      for (const q of effectiveQuestions) {
        if (!(q.qid in base)) base[q.qid] = undefined;
      }
      return base;
    });
    setSliderValues((prev) => {
      const base = { ...prev };
      for (const q of effectiveQuestions) {
        if (!(q.qid in base)) base[q.qid] = 5;
      }
      return base;
    });
  }, [effectiveQuestions]);

  // 進行バー（実際に答える数基準）
  const progressPosition = currentIndex + 1;
  const progressTotal = effectiveQuestions.length;
  const progressPercent = Math.round((progressPosition / progressTotal) * 100);

  const TRACK_LEFT_OFFSET = 24;
  const SLIDER_MIN = 1;
  const SLIDER_MAX = 10;
  const TICK_COUNT = SLIDER_MAX - SLIDER_MIN + 1;

  const ratio =
    (Number(sliderValues[currentQid] ?? 5) - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN);

  const sliderWrapRef = useRef(null);
  const clamp = (v) => Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, v));

  // 入力欄の一時文字列（現在qidに同期）
  const [inlineInput, setInlineInput] = useState(String(sliderValues[currentQid] ?? 5));
  useEffect(() => {
    setInlineInput(String(sliderValues[currentQid] ?? 5));
  }, [currentQid, sliderValues[currentQid]]);

  const handleSliderChange = (qid, value) => {
    setSliderValues((prev) => ({ ...prev, [qid]: value }));
  };
  const commitInlineInput = () => {
    const n = Number(inlineInput);
    if (!Number.isNaN(n)) handleSliderChange(currentQid, clamp(n));
    else setInlineInput(String(sliderValues[currentQid]));
  };
  const nudgeValue = (delta) => {
    setSliderValues((prev) => {
      const next = clamp((prev[currentQid] ?? SLIDER_MIN) + delta);
      setInlineInput(String(next));
      return { ...prev, [currentQid]: next };
    });
  };

  // スライダートラック幅の計測
  const measureTrack = useCallback(() => {
    const wrap = sliderWrapRef.current;
    if (!wrap) return;
    const trackEl =
      wrap.querySelector(".cds--slider__track") ||
      wrap.querySelector(".cds--slider__track-container") ||
      wrap.querySelector(".bx--slider__track") ||
      wrap.querySelector(".bx--slider__track-container");
    if (trackEl) {
      const rect = trackEl.getBoundingClientRect();
      setTrackWidth(rect.width);
    } else {
      const fallback = Math.max(wrap.clientWidth - TRACK_LEFT_OFFSET * 2, 200);
      setTrackWidth(fallback);
    }
  }, []);

  useEffect(() => {
    measureTrack();
    const ro = new ResizeObserver(() => measureTrack());
    if (sliderWrapRef.current) ro.observe(sliderWrapRef.current);
    const trackEl =
      sliderWrapRef.current?.querySelector(".cds--slider__track") ||
      sliderWrapRef.current?.querySelector(".cds--slider__track-container") ||
      sliderWrapRef.current?.querySelector(".bx--slider__track") ||
      sliderWrapRef.current?.querySelector(".bx--slider__track-container");
    if (trackEl) ro.observe(trackEl);
    window.addEventListener("resize", measureTrack);
    return () => {
      ro.disconnect(); 
      window.removeEventListener("resize", measureTrack);
    };
  }, [measureTrack]);

  const handleSkipToggle = (qid, checked) => {
    setDisabledSliders((prev) => ({ ...prev, [qid]: checked }));
    setSkipped((prev) => {
      const next = { ...prev };
      if (checked) next[qid] = true;
      else delete next[qid];
      return next;
    });
  };

const buildProhibitedSubIdList = (nextCategory, questionStates) => {
  if (nextCategory !== "2MSWS" && nextCategory !== "3MSWS") return [];

  const prohibited = [];

  for (const s of Object.values(questionStates)) {
    if (!s?.subId) continue;

    // 2MSWS → 1MSWS の subIDだけ禁止
    if (nextCategory === "2MSWS" && s.categoryId === "1MSWS") {
      prohibited.push(s.subId);
    }

    // 3MSWS → 1MSWS / 2MSWS の subIDを禁止
    if (
      nextCategory === "3MSWS" &&
      (s.categoryId === "1MSWS" || s.categoryId === "2MSWS")
    ) {
      prohibited.push(s.subId);
    }
  }

  return prohibited;
};

  const handleNext = async () => {
    const cqid = effectiveQuestions[currentIndex].qid;
    setAnswers((prev) => ({
      ...prev,
      [cqid]: disabledSliders[cqid] ? undefined : sliderValues[cqid],
    }));

    const nextIndex = currentIndex + 1;

    if (nextIndex < effectiveQuestions.length) {
      const nextQid = effectiveQuestions[nextIndex].qid;

      // ✅ すでに生成済みならAPI呼ばずにstateだけ切り替え
      if (questionStates[nextQid]?.text) {
        setCurrentIndex(nextIndex);
        return;
      }

      // まだ生成されていなければAPI呼び出し
      setIsGenerating(true);
      setCurrentIndex(nextIndex);

      const nextCategory = toCategoryId(nextQid)
      const prohibitedList = buildProhibitedSubIdList(nextCategory, questionStates);

      try {
        const selectRes = await api.post("/v1/questions/select", {
          category_id: nextCategory,
          // prohibited_list: [],
          prohibited_list: prohibitedList,
          patient_info: patientInfo,
        });
        const subId = selectRes.data.question_id;

        const personalizeRes = await api.post("/v1/questions/personalize", {
          category_id: nextCategory,
          sub_id: subId,
          patient_info: patientInfo,
          prev_q_list: [],
        });

        const newText = personalizeRes.data.personalized_question;
        setQuestionStates((prev) => ({
          ...prev,
          [nextQid]: { text: newText, subId, categoryId: nextCategory, regenCount: 0, prevTexts: [] },
        }));
      } catch (err) {
        console.error("次の質問生成エラー:", err);
      } finally {
        setIsGenerating(false);
      }
    } else {
      setConfirmOpen(true);
    }
  };

  const openConfirm = () => {
    const cqid = effectiveQuestions[currentIndex].qid;
    setAnswers((prev) => ({
      ...prev,
      [cqid]: disabledSliders[cqid] ? undefined : sliderValues[cqid],
    }));
    setConfirmOpen(true);
  };
  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  // 現在Qの再生成 残り回数・可否
  const currentRegenUsed = regenCounts[currentQid] ?? 0;
  const currentRegenRemain = Math.max(0, MAX_REGEN_PER_Q - currentRegenUsed);
  const canRegenCurrent = currentRegenRemain > 0;

  const handleRegenCurrent = async () => {
    if (!canRegenCurrent) return;

    const cqid = effectiveQuestions[currentIndex].qid;

    const categoryId = toCategoryId(cqid)
    const subId = questionStates[cqid]?.subId ?? 1;

    // 現在表示中の文章（= 今回 prev に入れたい対象）
    const currentText = questionStates[cqid]?.text ?? "";

    // これまでの履歴（初回/再生成の過去文）
    const prevTexts = questionStates[cqid]?.prevTexts ?? [];

    // APIに渡すprev_q_list：
    //   既存履歴 + 今回の“現在文” を追加（重複は必要なら除外）
    const prev_q_list = [...prevTexts, currentText]
      .filter((t) => t && t.trim().length > 0);

    try {
      setIsGenerating(true);

      const res = await api.post("/v1/questions/personalize", {
        category_id: categoryId,
        sub_id: subId,
        patient_info: patientInfo,
        prev_q_list,
      });

      const newText = res.data.personalized_question;

      setQuestionStates((prev) => {
        const old = prev[cqid] ?? {};
        const oldPrevTexts = old.prevTexts ?? [];

        // 送信した prev_q_list を “新しい履歴” として保持する
        // ただし再生成は最大2回想定なので、履歴は最大2つに制限
        const nextPrevTexts = prev_q_list.slice(0, 2);

        return {
          ...prev,
          [cqid]: {
            ...old,
            text: newText,
            regenCount: (old.regenCount ?? 0) + 1,
            prevTexts: nextPrevTexts,
          },
        };
      });

      setRegenCounts((prev) => ({
        ...prev,
        [cqid]: (prev[cqid] ?? 0) + 1,
      }));
    } catch (err) {
      console.error("再生成失敗:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  const isLast = currentIndex === effectiveQuestions.length - 1;

  const ActionButtons = () => (
    <>
      <div className="mobile-action-bar__left" style={{ alignItems: "flex-end" }}>
        {currentIndex > 0 && (
          <CustomButton kind="tertiary" size="md" onClick={handleBack} disabled={isGenerating}>
            戻る
          </CustomButton>
        )}
      </div>

      <div className="mobile-action-bar__right" style={{ alignItems: "flex-end", gap: "0.25rem" }}>
        {/* 再生成カラム：ボタンを下端に寄せる */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-end",
          }}
        >
          <div className="regen-remaining center">{`残り回数：あと${currentRegenRemain}回`}</div>
          <CustomButton
            kind="secondary"
            size="md"
            onClick={handleRegenCurrent}
            disabled={!canRegenCurrent || isGenerating}
            title={canRegenCurrent ? "" : "これ以上は再生成できません"}
          >
            質問を再生成
          </CustomButton>
        </div>

        {!isLast ? (
          <CustomButton kind="primary" size="md" onClick={handleNext} disabled={isGenerating}>
            次へ
          </CustomButton>
        ) : (
          <CustomButton kind="primary" size="md" onClick={openConfirm} disabled={isGenerating} ref={confirmPrimaryRef}>
            確認
          </CustomButton>
        )}
      </div>
    </>
  );

  // 表示カテゴリ名（大グループ名）
  const categoryLabel = (qid) => keyToGroup(qid); // 例: "歩行機能"

  const isLoading =
    !user || // ログイン情報まだ
    !startInfo || // start_aiまだ
    !patientInfo; // buildPatientInfoまだ

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          fontSize: "1.2rem",
          color: "#6f6f6f",
        }}
      >
        読み込み中です…
      </div>
    );
  }

  return (
    <>
      <div style={{ background: "#f4f4f4", minHeight: "100vh", padding: "1rem" }}>
        <div
          style={{
            maxWidth: "1000px",
            margin: "auto",
            padding: "1rem 2rem 1rem 2rem",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            paddingBottom: "2rem",
            transition: "padding-bottom 120ms ease",
          }}
        >
          {/* 進行バー（答える数基準） */}
          <div className="ai-progress" aria-label="進行状況">
            <div className="ai-progress__meta">
              <div className="ai-progress__percent" aria-live="polite">
                {progressPercent}%
              </div>
              <div className="ai-progress__count">
                現在: {progressPosition} / {progressTotal}
              </div>
            </div>
            <div
              className="ai-progress__track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPercent}
            >
              <div
                className="ai-progress__fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* スキップ告知（非歩行時） */}
          {nonAmbulatory && (
            <div
              style={{
                margin: "0.5rem 0 1rem",
                fontSize: "0.9rem",
                color: "#525252",
                background: "#f4f4f4",
                padding: "0.5rem 0.75rem",
                borderLeft: "4px solid #0f62fe",
              }}
            >
              歩行機能に関する質問（1問目〜3問目）はスキップされています。
            </div>
          )}

          {/* X問目（通し番号）：カテゴリ名（= 大グループ名） */}
          <h1 style={{ margin: "2rem 0 0.5rem 0", lineHeight: 1.4, fontSize: "1.4rem" }}>
            {positionOriginal}問目：{categoryLabel(currentQid) || "—"}
          </h1>
          <div style={{ color: "#6f6f6f", fontSize: "0.875rem", marginTop: 4 }}>
            全{totalOriginal}問
          </div>

          {/* 質問文 */}
          <div
            className="question-box"
            style={{ minHeight: 140, maxHeight: 240, overflowY: "auto", padding: "1.5rem 0 2rem 0" }}
          >
            {isGenerating ? (
              <p style={{ margin: 0, color: "#6f6f6f" }}>質問を生成中です…</p>
            ) : (
              <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {questionStates[currentQid]?.text || ""}
              </p>
            )}
          </div>

          <Checkbox
            id={`skip-${currentQid}`}
            labelText="この質問には回答しない"
            checked={!!disabledSliders[currentQid]}
            onChange={(e) =>
              setDisabledSliders((p) => ({ ...p, [currentQid]: e.target.checked }))
            }
            onClick={() => {
              /* クリック抑止なし */
            }}
            style={{ margin: "2rem 0" }}
          />
          {/* 実ステート反映 */}
          {disabledSliders[currentQid] !== !!skipped[currentQid] &&
            handleSkipToggle(currentQid, !!disabledSliders[currentQid])}

          {/* スライダー */}
          <div
            style={{
              display: isMobile ? "block" : "flex",
              alignItems: "flex-end",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            <div ref={sliderWrapRef} style={{ position: "relative", flex: 1 }}>
              {/* 値バブル */}
              <div
                className={`value-bubble ${
                  disabledSliders[currentQid] ? "value-bubble--disabled" : ""
                }`}
                style={{
                  left: `${24 + ratio * trackWidth}px`,
                  bottom: "34px",
                }}
                aria-hidden="true"
              >
                {disabledSliders[currentQid] ? "—" : sliderValues[currentQid]}
                <span className="value-bubble__arrow" />
              </div>

              {/* 現在値の丸マーカー */}
              <div
                className={`thumb-dot ${
                  disabledSliders[currentQid] ? "thumb-dot--disabled" : ""
                }`}
                style={{ left: `${24 + ratio * trackWidth}px` }}
                aria-hidden="true"
              />

              <Slider
                className="full-width-slider"
                min={1}
                max={10}
                step={1}
                value={sliderValues[currentQid]}
                onChange={({ value }) => handleSliderChange(currentQid, value)}
                disabled={!!disabledSliders[currentQid]}
                labelText=""
                hideTextInput
              />

              {/* 目盛り */}
              <div className="tick-overlay" style={{ width: `${trackWidth}px` }}>
                {Array.from({ length: TICK_COUNT }, (_, j) => (
                  <div
                    key={j}
                    className="tick"
                    style={{
                      left: `${(trackWidth * j) / (TICK_COUNT - 1)}px`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* フッター：左右ラベル + 中央の数値入力 */}
          <div
            className="slider-foot"
            style={{
              position: "relative",
              width: `${trackWidth}px`,
              marginLeft: `24px`,
              marginTop: "0.5rem",
              height: "64px",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 0,
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
              }}
            >
              全くない
            </span>
            <span
              style={{
                position: "absolute",
                right: 0,
                transform: "translateX(50%)",
                whiteSpace: "nowrap",
              }}
            >
              いつもそうだ
            </span>

            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                top: "2rem",
                width: isMobile ? "180px" : "200px",
              }}
            >
              <div
                className="inline-stepper"
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <CustomButton
                  kind="ghost"
                  size="md"
                  disabled={!!disabledSliders[currentQid]}
                  onClick={() => nudgeValue(-1)}
                  aria-label="1減らす"
                >
                  －
                </CustomButton>
                <input
                  className="mini-numinput"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={inlineInput}
                  disabled={!!disabledSliders[currentQid]}
                  onChange={(e) =>
                    setInlineInput(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  onBlur={commitInlineInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitInlineInput();
                    else if (e.key === "Escape")
                      setInlineInput(String(sliderValues[currentQid]));
                    else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      nudgeValue(+1);
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault();
                      nudgeValue(-1);
                    }
                  }}
                  aria-label="数値を入力"
                />
                <CustomButton
                  kind="ghost"
                  size="md"
                  disabled={!!disabledSliders[currentQid]}
                  onClick={() => nudgeValue(+1)}
                  aria-label="1増やす"
                >
                  ＋
                </CustomButton>
              </div>
            </div>
          </div>

          {!isMobile && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1rem",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              <ActionButtons />
            </div>
          )}
        </div>
      </div>

      {isMobile && (
        <div ref={barRef} className="mobile-action-bar" role="region" aria-label="操作メニュー">
          <ActionButtons />
        </div>
      )}

      {/* 確認モーダル（番号は通し基準） */}
      <CustomModal
        open={isConfirmOpen}
        title="この内容で送信しますか？"
        onClose={() => setConfirmOpen(false)}
        size={isMobile ? "sm" : "md"}
        closeOnOverlay
        initialFocusRef={confirmPrimaryRef}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <CustomButton kind="secondary" onClick={() => setConfirmOpen(false)}>
              キャンセル
            </CustomButton>
            <CustomButton
              kind="primary"
              onClick={handleFinalSubmit}
              ref={confirmPrimaryRef}
              data-autofocus
            >
              送信
            </CustomButton>
          </div>
        }
      >
        <div style={{ maxHeight: "50vh", overflowY: "auto", paddingBottom: "0.5rem" }}>
          {effectiveQuestions.map((q, i) => {
            const idxOriginal = baseOffset + i + 1;
            const qid = q.qid;
            const value = skipped[qid]
              ? "回答しない"
              : answers[qid] !== undefined
              ? answers[qid]
              : sliderValues[qid];
            return (
              <div
                key={qid}
                style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 8 }}
              >
                <div>
                  <strong>{idxOriginal}問目</strong>
                  <span style={{ marginLeft: 8 }}>{categoryLabel(qid)}</span>
                </div>
                <div>{String(value)}</div>
              </div>
            );
          })}
        </div>
      </CustomModal>
    </>
  );
}
